// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000; // 1 min

async function fetchPendingFromApi() {
  try {
    const { data } = await axios.get(`${API}/notifications/pending`);
    return data.notifications || [];
  } catch (err) {
    console.debug('Notifications fetch failed:', err);
    return null;
  }
}

async function dismissOnApi(id) {
  try {
    await axios.post(`${API}/notifications/dismiss/${id}`);
    return true;
  } catch (err) {
    console.debug('Dismiss failed:', err);
    return false;
  }
}

export function usePendingNotifications(enabled = true) {
  const [notifications, setNotifications] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setNotifications([]);
      return undefined;
    }
    let cancelled = false;

    fetchPendingFromApi().then((list) => {
      if (!cancelled && list !== null) setNotifications(list);
    });

    const id = setInterval(() => {
      if (!cancelled) setTick((t) => t + 1);
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || tick === 0) return undefined;
    let cancelled = false;
    fetchPendingFromApi().then((list) => {
      if (!cancelled && list !== null) setNotifications(list);
    });
    return () => { cancelled = true; };
  }, [tick, enabled]);

  // Returned helpers are plain async functions (no useCallback) — they're
  // invoked from event handlers, and the parent component re-renders cheap.
  async function dismiss(id) {
    const ok = await dismissOnApi(id);
    if (ok) setNotifications((prev) => prev.filter((n) => n.id !== id));
  }

  function refresh() {
    setTick((t) => t + 1);
  }

  return { notifications, refresh, dismiss };
}
